<script lang="ts">
  import type { ComponentType } from 'svelte';

  /**
   * The header every full-width screen wears.
   *
   * # Why one component
   *
   * There were five, and no two agreed: the guide's door centred an `h1` over a
   * paragraph, its steps put a progress bar above an `h1`, the order form and
   * the industry picker each used `.modal-header` with an `h2` and an `h3`
   * respectively, and the assistant had a coloured band of its own. Same job,
   * five sizes of heading, four paddings, two ideas about where the close
   * button goes.
   *
   * Screens that share a shape are read as one product. Screens that each
   * invent their own are read as parts that happen to be next to each other,
   * which is most of what "homemade" means.
   *
   * # What it is not for
   *
   * The data view. Results are not a screen the customer is being walked
   * through; they are the thing itself, and a header over them would be
   * chrome above chrome — the top bar is already there.
   */

  /**
   * A mark for the screen, shown large beside the title.
   *
   * The one place colour is spent on something that is not a state. Every
   * screen in this flow now looks the same by construction — same width, same
   * heading, same bands — which is what makes it feel like one product and
   * also what makes each screen hard to tell apart at a glance. A single
   * distinct mark per screen is cheap recognition, and it is the only icon
   * here: the decorative ones were removed precisely so this one reads.
   */
  export let icon: ComponentType | null = null;

  /** The one line that says where you are. */
  export let title: string;

  /** An optional second line: what this screen wants, or what it will do. */
  export let subtitle: string = '';

  /**
   * Position in a sequence, when there is one.
   *
   * Both are needed together — a step number without a total says "there is
   * more" without saying how much more, which is the anxious version of the
   * same information.
   */
  export let step: number = 0;
  export let steps: number = 0;

  $: showProgress = step > 0 && steps > 0;
</script>

<!--
  No band of its own.

  It was a white bordered strip over a grey canvas, which drew a line between
  the title and the thing it titles — and made the title look like chrome
  belonging to the window rather than the first line of the screen. On the
  canvas it reads as what it is: the heading of the page below it.
-->
<!--
  No width or padding of its own.

  ScreenView owns the column — one `max-w-5xl` wrapper holding the header and
  the card — so the two cannot disagree about where the left edge is. They did,
  when each constrained itself: identical classes in different layout boxes do
  not produce identical geometry once a scrollbar is involved.

  Always left-aligned, too. One screen centred its title while everything under
  it stayed left, so the heading sat thirty pixels right of the first line it
  described. The column is what is centred in the window, not the text in it.
-->
<header class="shrink-0">
  {#if showProgress}
    <!--
    The bar reads as a measure of how far through you are, so it sits above
    the title rather than beside it: you learn the shape of the task before
    you read what this part of it asks.
  -->
    <div class="mb-3 flex items-center gap-3">
      <div class="flex flex-1 items-center gap-1.5">
        {#each Array(steps) as _, index}
          <span
            class="h-1 flex-1 rounded-full transition-colors {index < step
              ? 'bg-accent-500'
              : 'bg-gray-200'}"
            aria-hidden="true"
          ></span>
        {/each}
      </div>
      <span class="shrink-0 text-2xs tabular-nums text-gray-500">{step}/{steps}</span>
    </div>
  {/if}

  <div class="flex items-start justify-between gap-4">
    <div class="flex min-w-0 items-center gap-4">
      {#if icon}
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-surface bg-accent-50 text-accent-600"
        >
          <svelte:component this={icon} class="h-6 w-6" />
        </div>
      {/if}

      <div class="min-w-0">
        <h1 class="truncate text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
        {#if subtitle}
          <p class="mt-1 text-sm text-gray-600">{subtitle}</p>
        {/if}
      </div>
    </div>

    <!--
    Actions sit at the end of the title row rather than in a footer, because
    on every one of these screens the action is "leave" — and leaving belongs
    next to the name of the thing you are leaving.
  -->
    <div class="flex shrink-0 items-center gap-2">
      <slot name="actions" />
    </div>
  </div>
</header>
